'use client';

import type { Theme, SxProps } from '@mui/material/styles';

import { useRef, useEffect } from 'react';

import Box from '@mui/material/Box';

import { brandCubeSpec, renderBrandCube } from './brand-cube-render';

import type { BrandCubeAnim } from './brand-cube-render';

// ----------------------------------------------------------------------

export type BrandCubeProps = {
  /** 모션. `static` 은 맞춰진 정지 화면, `loader` 는 바탕 없는 로딩 스피너다 */
  anim?: BrandCubeAnim;
  /** 한 변(px) */
  size?: number;
  /** 둥근 사각 바탕(앱 아이콘)을 깐다 — 기본은 `loader` 만 끈다 */
  background?: boolean;
  /** 어두운 바탕 위에 둘 때 */
  dark?: boolean;
  /** 지정하면 `role="img"` 로 읽힌다. 없으면 장식으로 숨긴다 */
  label?: string;
  /** 큐브 확대 배율 — 바탕 없이 작은 칸에 둘 때 칸을 채운다(기본 1) */
  fit?: number;
  /** 빛 세기 배율 — 작은 칸에서 `shimmer` 가 묻힐 때(기본 1) */
  glowGain?: number;
  sx?: SxProps<Theme>;
};

/**
 * CatchMoments 큐브 아이콘을 캔버스로 그린다. 모션은 화면에 보일 때만 돌고,
 * 「동작 줄이기」 설정이면 맞춰진 정지 화면만 보여 준다.
 *
 * 🔑 시각은 마운트 시점이 아니라 **페이지 시계**(`performance.now`)다 — 로딩 경계가 갈아 끼워져 다시
 *   마운트되거나 두 개가 겹쳐 떠도, 모션이 처음부터 다시 돌지 않고 한 흐름으로 이어진다.
 */
export function BrandCube({ anim = 'static', size = 40, background, dark = false, label, fit = 1, glowGain = 1, sx }: BrandCubeProps) {
  const ref = useRef<HTMLCanvasElement>(null);
  const bg = background ?? anim !== 'loader';

  useEffect(() => {
    const cv = ref.current;
    const ctx = cv?.getContext('2d');
    if (!cv || !ctx) return undefined;

    const spec = brandCubeSpec(anim);
    const dpr = Math.min(3, window.devicePixelRatio || 1);
    cv.width = Math.round(size * dpr);
    cv.height = Math.round(size * dpr);
    let off: HTMLCanvasElement | null = null;
    const offscreen = () => {
      off ??= document.createElement('canvas');
      off.width = cv.width;
      off.height = cv.height;
      return off;
    };
    const paint = (t: number) => renderBrandCube(ctx, size, anim, t, { bg, dark, dpr, offscreen, fit, glowGain });

    paint(spec.rest);
    if (anim === 'static') return undefined;

    // matchMedia 가 없는 환경(jsdom 등)은 모션을 줄이지 않는 것으로 본다
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    let visible = true;
    const io =
      'IntersectionObserver' in window
        ? new IntersectionObserver((es) => {
            visible = es.some((e) => e.isIntersecting);
          })
        : null;
    io?.observe(cv);

    let raf = 0;
    const loop = (now: number) => {
      if (visible && !reduce?.matches) paint((now / 1000) % spec.T);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    const onReduce = () => paint(spec.rest);
    reduce?.addEventListener('change', onReduce);

    return () => {
      cancelAnimationFrame(raf);
      io?.disconnect();
      reduce?.removeEventListener('change', onReduce);
    };
  }, [anim, size, bg, dark, fit, glowGain]);

  return (
    <Box
      component="canvas"
      ref={ref}
      {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
      sx={[{ display: 'block', width: size, height: size, flexShrink: 0 }, ...(Array.isArray(sx) ? sx : [sx])]}
    />
  );
}
